import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import {
  BugReport as QualityIcon,
  AccountTree as RelationsIcon,
  Collections as CollectionsIcon,
  Extension as CompatibilityIcon,
  EditNote as BulkIcon,
  Label as SystemsIcon,
  ArrowForward as ArrowIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Panel from '../../components/common/Panel';
import { SHADOWS, hoverOnly } from '../../theme/tokens';

/**
 * Overview Tab - Enhanced with Quick Navigation
 *
 * Provides a landing page with quick access to all Taxonomy Hub features.
 * Instead of complex metrics that require additional APIs, this provides
 * clear navigation and guidance for users.
 */
export default function OverviewTab() {
  const [, setSearchParams] = useSearchParams();

  const navigateToTab = (tabValue) => {
    setSearchParams({ tab: tabValue });
  };

  // Ein Rollen-Token für alle sechs Kacheln. Die sechs Palettenslots
  // (error/success/info/warning/primary/secondary) trugen keine Semantik —
  // es sind Wegweiser, keine Zustaende. Farbe kommt aus der Kachel (sx).
  const features = [
    {
      title: 'Quality Inspector',
      icon: <QualityIcon fontSize="large" />,
      description: 'Identify and fix data quality issues, conflicts, and missing taxonomy',
      tab: 'quality',
    },
    {
      title: 'Relations Manager',
      icon: <RelationsIcon fontSize="large" />,
      description: 'View and curate product relationships, manage recommendations',
      tab: 'relations',
    },
    {
      title: 'Collections Manager',
      icon: <CollectionsIcon fontSize="large" />,
      description: 'Manage Shopify dynamic collections and sync rules',
      tab: 'collections',
    },
    {
      title: 'Compatibility Manager',
      icon: <CompatibilityIcon fontSize="large" />,
      description: 'Define explicit compatibility rules for accessories and bundles',
      tab: 'compatibility',
    },
    {
      title: 'Bulk Editor',
      icon: <BulkIcon fontSize="large" />,
      description: 'Edit taxonomy attributes for multiple products at once',
      tab: 'bulk',
    },
    {
      title: 'Systems & Sync',
      icon: <SystemsIcon fontSize="large" />,
      description: 'Learn about the taxonomy system and Shopify sync',
      tab: 'systems',
    },
  ];

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 4 }}>
        {/* 🔴 F6 (UI-Durchgang 2, 2026-10-07): hier stand `variant="h4"` — die
            Zeile war damit GROESSER als der Seitentitel des `ViewHeader`
            (`variant="h5"`, 24 px gegen 34 px). Der Fotobefund nannte genau
            das: „Hierarchie invertiert". Die Hausform fuer einen Abschnitt
            unter der Seitenueberschrift ist `component="h2"` (der TAG) plus
            `variant="h6"` (das Aussehen) — dieselbe Trennung, die
            `ViewHeader` fuer seinen `h1` benutzt. Der Tag zieht damit die
            Gliederung nach (h1 → h2), die Groesse bleibt unter dem Titel. */}
        <Typography component="h2" variant="h6" gutterBottom>
          Welcome to the Taxonomy Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your central platform for managing product taxonomy, relations, collections, and compatibility rules.
        </Typography>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Getting Started:</strong> Use the tabs above to navigate, or click any card below to jump directly to that feature.
          For a complete guide to the taxonomy system, visit the <strong>Systems & Sync</strong> tab.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {features.map((feature) => (
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={feature.tab}>
            <Panel
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                // K-5 (Welle 2): reine Wirkung (Schatten + Transform) ohne Hintergrund.
                ...hoverOnly({ boxShadow: SHADOWS.panelHover, transform: 'translateY(-4px)' }),
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  endIcon={<ArrowIcon />}
                  onClick={() => navigateToTab(feature.tab)}
                  // Eine linke Kante je Kachel: die Beschriftung beginnt auf der
                  // Titelkante. `sizeSmall` traegt links 5 px (Theme unterhalb sm
                  // 14 px) — hier auf 0 gezogen, damit Symbol, Titel und Knopf
                  // auf derselben Linie stehen.
                  sx={{ pl: 0 }}
                >
                  Open {feature.title}
                </Button>
              </Box>
            </Panel>
          </Grid>
        ))}
      </Grid>

      {/* Quick Start Guide */}
      <Panel pad="card" sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Start Guide
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              1. Check Data Quality
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start with the <strong>Quality Inspector</strong> to find products missing taxonomy or with conflicts.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              2. Fix Issues in Bulk
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the <strong>Bulk Editor</strong> to assign canonical types to multiple products at once.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              3. Manage Product Relations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and curate product recommendations in the <strong>Relations Manager</strong>.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              4. Set Up Collections
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and sync dynamic Shopify collections in the <strong>Collections Manager</strong>.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              5. Define Compatibility
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set explicit compatibility rules for accessories in the <strong>Compatibility Manager</strong>.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              6. Learn the System
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read the comprehensive guide in <strong>Systems & Sync</strong> to understand how everything works together.
            </Typography>
          </Box>
        </Stack>
      </Panel>

      {/* System Architecture Overview */}
      <Panel pad="card" sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          How the Taxonomy Hub Works
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The Taxonomy Hub manages four interconnected systems:
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography variant="h6" color="primary.main">1</Typography>
              </Box>
              <Typography variant="subtitle2" gutterBottom>
                Product Taxonomy
              </Typography>
              <Typography variant="caption" color="text.secondary">
                AI-enriched categorization with 50+ canonical types
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography variant="h6" color="primary.main">2</Typography>
              </Box>
              <Typography variant="subtitle2" gutterBottom>
                Product Relations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Automated recommendations with manual overrides
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography variant="h6" color="primary.main">3</Typography>
              </Box>
              <Typography variant="subtitle2" gutterBottom>
                Shopify Collections
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dynamic collections auto-maintained via rules
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1,
                }}
              >
                <Typography variant="h6" color="primary.main">4</Typography>
              </Box>
              <Typography variant="subtitle2" gutterBottom>
                Compatibility Rules
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Explicit constraints for accessories
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Panel>
    </Box>
  );
}
